/** @jsx jsx */
import {
  Box,
  Button,
  Col,
  Container,
  Row,
  Title,
} from '@bottlebooks/gatsby-theme-base';
import { Plural, Trans } from '@lingui/macro';
import { graphql } from 'gatsby';
import React from 'react';
import { jsx } from 'theme-ui';
import EmptyState from '../ExhibitorsPage/EmptyState';
import FiltersToggleButton from '../Filters/FiltersToggleButton';
import createFacets from '../Filters/Product/productFacets';
import ProductFacetsBar from '../Filters/Product/ProductFacetsBar';
import reducer, {
  initialState,
  searchTermChanged,
} from '../Filters/Product/productFilterReducer';
import ProductFilters from '../Filters/Product/ProductFilters';
import ProductSearchTermFacet from '../Filters/Product/ProductSearchTermFacet';
import useFilter from '../Filters/useFilter';
import useSyncedLocation, {
  getStateFromLocation,
} from '../Filters/useSyncedLocation';
import Layout from '../Layout/';
import ListRow from '../List/ListRow';
import ProductListRow from '../ProductsList/ProductListRow';
import ProductsList from '../ProductsList/ProductsList';
import SEO from '../seo';
import useSearch from './useProductSearch';

const PAGE_SIZE = 30;

export default function ProductsPage({
  products,
  event,
  getProductUrl,
  location,
  paths = {},
  facetConfig,
  locale,
}) {
  const stateFromLocation = getStateFromLocation(location, initialState);
  const [state, dispatch] = React.useReducer(reducer, stateFromLocation);
  // Change the URL hash when the filters change.
  useSyncedLocation(state, { initialState, location });
  // Filter the products by term and facets.
  const searchedProducts = useSearch(products, state.searchTerm);
  const { items, facets } = useFilter(searchedProducts, {
    state,
    facetDefinitions: createFacets(facetConfig, state),
    // sortOptions,
  });
  const [limit, setLimit] = React.useState(PAGE_SIZE);
  const [filtersHidden, setFiltersHidden] = React.useState(true);
  function dispatchFilter(event, isActive) {
    dispatch(event);
    setLimit(PAGE_SIZE);
    if (isActive) setFiltersHidden(true);
  }
  const limitedItems = items.slice(0, limit);
  const hasMore = items.length > limitedItems.length;
  const page = {
    path: location.href,
  };
  return (
    <Layout paths={paths} locale={locale}>
      <SEO page={page} />

      <Container fluid sx={{ maxWidth: 'container.fluid' }}>
        <SearchHeader
          totalCount={products.length}
          count={items.length}
          sx={{ paddingTop: [4, 5], paddingBottom: [3, 4] }}
        />
      </Container>
      <Box sx={{ backgroundColor: 'light' }}>
        <Container fluid sx={{ maxWidth: 'container.fluid' }}>
          <ProductFacetsBar
            dispatch={dispatch}
            state={state}
            sx={{ marginBottom: 5 }}
          />
        </Container>
      </Box>
      <Container fluid sx={{ marginBottom: 6, maxWidth: 'container.fluid' }}>
        <Row>
          <Col
            flex
            sx={{
              marginRight: [null, null, null, `${100 / 12}%`],
              marginTop: 2,
            }}
          >
            <ProductSearchTermFacet
              value={state.searchTerm}
              onChange={value => dispatch(searchTermChanged(value))}
            />
            <FiltersToggleButton
              onToggle={() => setFiltersHidden(!filtersHidden)}
              filtersHidden={filtersHidden}
              sx={{
                display: ['flex', null, 'none'],
                marginTop: -3,
                marginBottom: 3,
              }}
            />
            <ProductFilters
              facets={facets}
              dispatch={dispatchFilter}
              state={state}
              totalCount={products.length}
              count={items.length}
              sx={{
                display: [filtersHidden ? 'none' : null, null, 'block'],
              }}
            />
          </Col>
          <Col sx={{ width: ['100%', null, `${(100 / 12) * 7}%`] }}>
            {!items || !items.length ? (
              <EmptyState />
            ) : (
              <ProductsList sx={{ marginX: [-3, -2], marginBottom: -2 }}>
                {limitedItems.map(product => (
                  <ProductListRow
                    key={product.id}
                    product={product}
                    to={getProductUrl?.(product)}
                    sx={{ margin: [0, 2] }}
                  />
                ))}
                {hasMore && (
                  <Button
                    as={ListRow}
                    variant="empty"
                    onClick={() => setLimit(limit + PAGE_SIZE)}
                    sx={{
                      margin: [0, 2],
                      paddingX: 4,
                      paddingY: 3,
                      textAlign: 'center',
                      display: 'block',
                      border: 0,

                      variant: 'text.small',
                    }}
                  >
                    <Trans>Show more</Trans>
                  </Button>
                )}
              </ProductsList>
            )}
          </Col>
        </Row>
      </Container>
    </Layout>
  );
}

function SearchHeader({ totalCount, count, ...rest }) {
  return count === totalCount ? (
    <Title variant="large" {...rest}>
      <Plural
        value={totalCount}
        one={
          <React.Fragment>
            Showing you <Em>#</Em> product
          </React.Fragment>
        }
        other={
          <React.Fragment>
            Showing you <Em>#</Em> products
          </React.Fragment>
        }
      />
    </Title>
  ) : (
    <Title variant="large" {...rest}>
      <Plural
        value={totalCount}
        one={
          <React.Fragment>
            Showing you <Em>{count}</Em> of <Em>#</Em> product
          </React.Fragment>
        }
        other={
          <React.Fragment>
            Showing you <Em>{count}</Em> of <Em>#</Em> products
          </React.Fragment>
        }
      />
    </Title>
  );
}

function Em({ children, ...rest }) {
  return <em sx={{ color: 'primary', fontStyle: 'normal' }}>{children}</em>;
}

export const fragment = graphql`
  fragment ProductsPage on Product {
    id
    features # The features have to be queried outside of the list row because they are not typically shown.
    ...ProductListRow
    ...Search_Product
    ...ProductFilters
  }
  fragment ProductsPage_first on Product {
    ...ProductListRow_first
  }
`;
